import {Button, Input} from '@sportcast/ui-react';
import {FormEventHandler, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import login from '../../fetch/login';
import {getJsonDataNoError} from '../../fetch/requests';
import './Login.scss';

type LoginObject = {
    username: string,
    password: string
}

type LoginValidation = {
    username: boolean,
    password: boolean
}

const Login = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const [loginData, setLoginData] = useState<LoginObject>({} as LoginObject);
    const [showErrors, setShowErrors] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);
    const [loginInactive, setLoginInactive] = useState(false);
    const [loginBlocked, setLoginBlocked] = useState(false);
    const [isInternalUser, setIsInternalUser] = useState(false);
    const [loginValidation, setLoginValidation] = useState<LoginValidation>({} as LoginValidation);

    const onChange = (id: string, target: string | number) => {
        setLoginData({
            ...loginData,
            [id]: target
        });
        setLoginFailed(false);
        setLoginInactive(false);
        setLoginBlocked(false);

        if (id === 'username' && typeof target === 'string') {
            const isInternal =
                target.includes('@sportcast.de') ||
                target.includes('@sportcast.intern');
            setIsInternalUser(isInternal);
        }
    };

    const validate = useCallback(() => {
        setLoginValidation({
            username: !!loginData.username,
            password: !!loginData.password
        });
    }, [loginData]);

    useEffect(() => {
        validate();
    }, [validate]);

    const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();

        if (Object.values(loginValidation).some(value => !value)) {
            setShowErrors(true);
            return;
        }

        login(loginData?.username, loginData?.password, dispatch).then((resp: Response) => {
            if (resp.redirected) {
                const url = new URL(resp.url);
                const isError = url.searchParams.has('error') || url.search.indexOf('error') >= 0;

                if (!isError) {
                    // request me to get the SPCSSO cookie set
                    getJsonDataNoError('api/1.0/accounts/me', dispatch).then(() => window.location.href = resp.url);

                } else {
                    if (url.searchParams.get('error') === 'inactive') {
                        setLoginInactive(true);
                    } else if (url.searchParams.get('error') === 'blocked') {
                        setLoginBlocked(true);
                    } else {
                        // check if login or redirect failed
                        getJsonDataNoError('api/1.0/accounts/me', dispatch)
                            .then(() => window.location.reload())
                            .catch(() => setLoginFailed(true));
                    }
                }
                return;
            }
        }).catch(() => {
            // check if login or redirect failed
            getJsonDataNoError('api/1.0/accounts/me', dispatch)
                .then(() => window.location.reload())
                .catch(() => setLoginFailed(true));
        });
    };

    const getErrorMessage = (field: 'username' | 'password') => {
        if(field === 'username'){
            if (loginInactive ) {
                return t('validation.INACTIVE_LOGIN');
            }
            if (loginBlocked ) {
                return t('validation.BLOCKED_LOGIN');
            }
            if (loginFailed) {
                return t('validation.INVALID_LOGIN_DATA');
            }
            if (!showErrors) {
                return undefined;
            }
        }
        if(field === 'password'){
            if(loginInactive || loginBlocked){
                return undefined;
            }
            if (loginFailed) {
                return t('validation.INVALID_LOGIN_DATA');
            }
            if (!showErrors) {
                return undefined;
            }
        }

        return !loginValidation[field] ? t('validation.REQUIRED') : undefined;
    };
    /*
    Set onBlur={() => {}} to avoid calls to onchange with undefined values
     */
    return (
        <>
            <form onSubmit={handleSubmit} className="px-3 pt-5 login">
                <div className="username-field">
                    <Input
                        id="username"
                        type={'text'}
                        label={t(`login.EMAIL_USERNAME`)}
                        required={true}
                        autoComplete="username"
                        value={loginData.username}
                        onChange={onChange}
                        errorMessage={getErrorMessage('username') ? ' ' : undefined}
                    />
                    <div className="username-hint text-end text-muted small">{t('login.USERNAME_HINT')}</div>
                    {getErrorMessage('username') && (
                        <div className="username-error text-danger small">{getErrorMessage('username')}</div>
                    )}
                </div>
                <Input
                    id="password"
                    type="password"
                    label={t('login.PASSWORD')}
                    required={true}
                    value={loginData.password}
                    onChange={onChange}
                    errorMessage={getErrorMessage('password')}
                />
                {loginFailed && !isInternalUser && (
                    <div className="text-end forgot-password">
                        <Link to={'/forgot-password'}>{t('login.FORGOT_PASSWORD')}</Link>
                    </div>
                )}
                <div className="button-footer pb-3 text-center">
                    <Button
                        id="login"
                        className="w-100"
                        variant="primary"
                        type="submit"
                        label={t('login.LOGIN')}
                    />
                </div>
                <div className="d-flex pb-3"></div>
            </form>
        </>
    );
};

export default Login;